/**
 * TeamLeader write surface — wraps the backend `api/teamleader/*` endpoints.
 *
 * Every endpoint is authorized `SystemAdmin,TeamLeader` and scoped per-group on the server via
 * AccessScopeService.CanManageGroupAsync, so these calls only ever succeed for groups the caller
 * leads. Shapes: GroupDto is camelCase (default policy); the picker/lookup DTOs and the certificate
 * request are snake_case (explicit [JsonPropertyName] on the server).
 */
import { API_BASE_URL, getAuthHeaders } from "@/lib/api-client";

export interface TeamLeaderGroup {
  id: string;
  name: string;
  description: string | null;
  batchId: string | null;
  batchName: string | null;
  teamLeaderId: string | null;
  teamLeaderName: string | null;
  teamLeaderEmail: string | null;
  courses: { id: string; name: string }[];
  tracks: { id: string; name: string }[];
  createdAt: string;
  updatedAt: string | null;
}

export interface NamedRef {
  id: string;
  name: string;
}

/** Query for either assignable-content picker. `groupId` drives the already-linked flags. */
export interface AssignableQuery {
  groupId?: string;
  search?: string;
  pageNumber?: number;
  pageSize?: number;
}

/** Server envelope for the paged picker endpoints (PagedResultDto, camelCase). */
export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface AssignableCourse {
  id: string;
  name: string;
  /** False only for the caller's OWN drafts — other authors' drafts are never returned. */
  is_published: boolean;
  module_count: number;
  /** True when the course already reaches the group passed as `groupId`. */
  already_linked: boolean;
  /** `"direct"` or `"track:<name>"`; null when not already linked. */
  linked_via: string | null;
}

export interface AssignableTrack {
  id: string;
  name: string;
  /** Courses in the track the CALLER may see, so an all-drafts track reads as empty. */
  course_count: number;
  already_linked: boolean;
}

export interface StudentLookup {
  id: string;
  full_name: string | null;
  email: string | null;
}

export interface IssueCertificatePayload {
  student_id: string;
  cert_type: "course" | "track";
  source_id: string;
  custom_title?: string | null;
  custom_hours?: number | null;
}

function base(): string {
  if (!API_BASE_URL) throw new Error("API base URL not configured");
  return API_BASE_URL;
}

// Shared request helper: attaches auth, surfaces the server's error message, and tolerates
// empty (204) bodies.
async function request<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${base()}${path}`, {
    ...init,
    headers: { ...getAuthHeaders(), ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      message = body?.message || body?.error || message;
    } catch {
      const text = await res.text().catch(() => "");
      if (text) message = text;
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// ── Read helpers ───────────────────────────────────────────────────────────
export const fetchMyGroups = () => request<TeamLeaderGroup[]>("/teamleader/groups");

/** Lightweight aggregate with student counts. One request replaces 4 × N count probes. */
export interface GroupSummary {
  id: string;
  name: string;
  description: string | null;
  studentCount: number;
  activeCount: number;
  completedCount: number;
  atRiskCount: number;
}

export const fetchGroupSummaries = () =>
  request<GroupSummary[]>("/teamleader/group-summaries");

export const fetchMyGroupDetails = (groupId: string) =>
  request<TeamLeaderGroup>(`/teamleader/groups/${groupId}`);

/** One progress figure: the modules completed out of those assigned, and the two counts behind it. */
export interface ProgressCell {
  completedModules: number;
  totalModules: number;
  percent: number;
}

export interface GroupProgressStudent {
  id: string;
  fullName: string | null;
  email: string | null;
  studentCode: string | null;
  status: string;
  overall: ProgressCell;
  /**
   * Keyed by track / course id. A key is ABSENT when the student was never given that track or
   * course, which the export renders as a blank cell rather than 0%.
   */
  tracks: Record<string, ProgressCell>;
  courses: Record<string, ProgressCell>;
}

export interface GroupProgressExport {
  groupId: string;
  groupName: string;
  generatedAt: string;
  /** Column order for the per-track columns. */
  tracks: { id: string; name: string }[];
  /** Column order for the per-course columns; `trackIds` is empty for a directly-attached course. */
  courses: { id: string; name: string; trackIds: string[] }[];
  students: GroupProgressStudent[];
}

/** The whole group's progress report in one call — see the CSV builder on the group page. */
export const fetchGroupProgressExport = (groupId: string) =>
  request<GroupProgressExport>(`/teamleader/groups/${groupId}/progress-export`);

// ── Assignable-content catalogue (the "link content to my group" picker) ────
//
// Org-wide by design: a leader may deliver ANY ContentCreator's course or track to the group they
// lead, so this is deliberately NOT filtered to content the caller authored. What the server DOES
// withhold is other authors' unpublished drafts — linking one would create an assignment for content
// students never see. The caller's own drafts stay selectable (flagged `is_published: false`).
//
// Paged + searchable because this is the whole organisation's catalogue, not the caller's own shelf.
// Pass `groupId` to have every row flagged with whether it already reaches that group and how.

function assignableQuery(params: AssignableQuery): string {
  const qs = new URLSearchParams();
  if (params.groupId) qs.set("groupId", params.groupId);
  if (params.search?.trim()) qs.set("search", params.search.trim());
  qs.set("pageNumber", String(params.pageNumber ?? 1));
  qs.set("pageSize", String(params.pageSize ?? 20));
  return qs.toString();
}

export const fetchAssignableTracks = (params: AssignableQuery = {}) =>
  request<PagedResult<AssignableTrack>>(`/teamleader/assignable/tracks?${assignableQuery(params)}`);

export const fetchAssignableCourses = (params: AssignableQuery = {}) =>
  request<PagedResult<AssignableCourse>>(
    `/teamleader/assignable/courses?${assignableQuery(params)}`,
  );

/**
 * Every page of a catalogue, for the flat dropdowns that predate paging (exam targeting, the
 * group-management tab). Walks the pages rather than requesting one huge page: the server clamps
 * pageSize to 100, so a single call would silently truncate a larger catalogue — and a dropdown
 * that quietly omits options is worse than a slightly slower one.
 */
async function fetchAllPages<T>(
  fetchPage: (params: AssignableQuery) => Promise<PagedResult<T>>,
): Promise<T[]> {
  const pageSize = 100;
  const all: T[] = [];
  for (let pageNumber = 1; ; pageNumber++) {
    const page = await fetchPage({ pageNumber, pageSize });
    all.push(...page.items);
    if (all.length >= page.totalCount || page.items.length === 0) break;
  }
  return all;
}

export const fetchAllAssignableTracks = () => fetchAllPages(fetchAssignableTracks);

export const fetchAllAssignableCourses = () => fetchAllPages(fetchAssignableCourses);

export async function lookupStudentByEmail(email: string): Promise<StudentLookup | null> {
  try {
    return await request<StudentLookup>(
      `/teamleader/students/lookup?email=${encodeURIComponent(email)}`,
    );
  } catch {
    return null; // 404 (no such student) resolves to null for the caller
  }
}

// ── Group info ───────────────────────────────────────────────────────────────
export const updateMyGroupInfo = (
  groupId: string,
  body: { name: string; description?: string | null },
) =>
  request<TeamLeaderGroup>(`/teamleader/groups/${groupId}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });

// ── Membership ─────────────────────────────────────────────────────────────
export const addStudentToGroup = (groupId: string, studentId: string) =>
  request(`/teamleader/groups/${groupId}/students`, {
    method: "POST",
    body: JSON.stringify({ studentId }),
  });

export const removeStudentFromGroup = (groupId: string, studentId: string) =>
  request(`/teamleader/groups/${groupId}/students/${studentId}`, { method: "DELETE" });

export const transferStudent = (body: {
  fromGroupId: string;
  toGroupId: string;
  studentId: string;
}) => request(`/teamleader/students/transfer`, { method: "POST", body: JSON.stringify(body) });

export interface CsvImportResult {
  totalRows: number;
  successCount: number;
  /** Existing students added to the target group (a student may be in several groups). */
  linkedExistingCount?: number;
  /** Rows whose student was already a member of the target group. */
  alreadyInGroupCount?: number;
  failureCount: number;
  errors: { row: number; email: string; errorMessage: string }[];
}

// Bulk-import students into a managed group. Distinct from the admin importGroupStudentsCsv in
// students.ts: this hits the TeamLeader-scoped endpoint (guarded per-group on the server).
export async function importStudentsCsvToGroup(
  groupId: string,
  file: File,
): Promise<CsvImportResult> {
  const b = base();
  const form = new FormData();
  form.append("file", file);
  // Multipart: send ONLY Authorization and let the browser set the multipart Content-Type/boundary.
  const headers = getAuthHeaders() as Record<string, string>;
  delete headers["Content-Type"];
  const res = await fetch(`${b}/teamleader/groups/${groupId}/import-csv`, {
    method: "POST",
    headers,
    body: form,
  });
  if (!res.ok) {
    let message = `Import failed (${res.status})`;
    try {
      const body = await res.json();
      message = body?.message || body?.error || message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return (await res.json()) as CsvImportResult;
}

// ── Training assignment ──────────────────────────────────────────────────────
export const assignTrackToGroup = (groupId: string, trackId: string) =>
  request(`/teamleader/groups/${groupId}/tracks/${trackId}`, { method: "POST" });

export const removeTrackFromGroup = (groupId: string, trackId: string) =>
  request(`/teamleader/groups/${groupId}/tracks/${trackId}`, { method: "DELETE" });

export const assignCourseToGroup = (groupId: string, courseId: string) =>
  request(`/teamleader/groups/${groupId}/courses/${courseId}`, { method: "POST" });

export const removeCourseFromGroup = (groupId: string, courseId: string) =>
  request(`/teamleader/groups/${groupId}/courses/${courseId}`, { method: "DELETE" });

/** What a bulk group-removal actually did. `not_in_group` ids were skipped, not failures. */
export interface BulkGroupRemovalResult {
  requested: number;
  removed: number;
  not_in_group: string[];
}

/**
 * Bulk-removes students from a group. Membership only — accounts, progress and issued
 * certificates all survive; only this group's derived course access is revoked.
 */
export const removeStudentsFromGroup = (
  groupId: string,
  studentIds: string[],
): Promise<BulkGroupRemovalResult> =>
  request(`/teamleader/groups/${groupId}/students/bulk-remove`, {
    method: "POST",
    body: JSON.stringify({ studentIds }),
  }).then((r: any) => ({
    requested: r?.requested ?? studentIds.length,
    removed: r?.removed ?? 0,
    not_in_group: r?.notInGroup ?? r?.not_in_group ?? [],
  }));

/**
 * "Complete Study" for a group member. `courseId` narrows it to one course in their track.
 * Waives the course's checkpoint/final-exam gates (recorded as a waiver, not a fake attempt)
 * and issues the certificates completion earns.
 */
export const forceCompleteStudy = (groupId: string, studentId: string, courseId?: string) =>
  request(
    `/teamleader/groups/${groupId}/students/${studentId}/force-complete` +
      (courseId ? `?courseId=${encodeURIComponent(courseId)}` : ""),
    { method: "POST" },
  );

/** `courseId` narrows the reset to one course in the student's track; omit it to reset everything. */
export const resetStudentProgress = (
  groupId: string,
  studentId: string,
  scope: string,
  courseId?: string,
) =>
  request(`/teamleader/groups/${groupId}/students/${studentId}/reset`, {
    method: "POST",
    body: JSON.stringify({ scope, courseId: courseId ?? null }),
  });

// ── Certificates (eligibility enforced server-side) ──────────────────────────
export const issueCertificate = (payload: IssueCertificatePayload) =>
  request(`/teamleader/certificates/issue`, { method: "POST", body: JSON.stringify(payload) });

export const reissueCertificate = (certificateId: string) =>
  request(`/teamleader/certificates/${certificateId}/reissue`, { method: "POST" });

// ── Messaging (own groups only; never system-wide) ───────────────────────────
export const messageGroup = (groupId: string, content: string, requiresAcknowledgment = false) =>
  request(`/teamleader/groups/${groupId}/message`, {
    method: "POST",
    body: JSON.stringify({ content, requiresAcknowledgment }),
  });

export const broadcastToMyGroups = (content: string, requiresAcknowledgment = false) =>
  request<{ message: string; groups: number }>(`/teamleader/broadcast`, {
    method: "POST",
    body: JSON.stringify({ content, requiresAcknowledgment }),
  });

// ── Mentoring notes (per-student; distinct from the admin Leads/CRM pipeline) ─
export interface StudentNote {
  id: string;
  studentId: string;
  authorId: string;
  authorName: string | null;
  content: string;
  noteType: string | null;
  createdAt: string;
}

export const fetchStudentNotes = (studentId: string) =>
  request<StudentNote[]>(`/teamleader/students/${studentId}/notes`);

export const addStudentNote = (studentId: string, content: string, noteType?: string) =>
  request<StudentNote>(`/teamleader/students/${studentId}/notes`, {
    method: "POST",
    body: JSON.stringify({ content, noteType }),
  });
